import { faChevronDown, faChevronLeft, faChevronRight, faChevronUp, faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import { fetchAllRecipes } from './api';
import FilterPanel from './FilterPanel';
import RecipeCard from './Recipe-card';

function AllRecipes({ initialFilters = null, showFilterPanel = true, layout = 'carousel', pageSize = 15 }) {
  const [allRecipes, setAllRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [cardsPerView, setCardsPerView] = useState(3);
  const containerRef = useRef(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragDelta, setDragDelta] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState(initialFilters || {
    portionSize: '',
    cuisine: '',
    highProtein: false,
    budgetFriendly: false,
    vegan: 'all',
    maxCalories: '',
    difficulty: '',
    prepTime: ''
  });

  useEffect(() => {
    fetchAllRecipes()
      .then((fetchedAllRecipes) => {
        setAllRecipes(fetchedAllRecipes);
        setFilteredRecipes(fetchedAllRecipes);
        // derive cuisine options
        const cuisines = Array.from(new Set(fetchedAllRecipes.map(r => (r.cuisine || '').toString().trim()).filter(Boolean)));
        setCuisineOptions(cuisines.sort());
      })
      .catch((error) => {
        console.error('Error fetching all recipes:', error);
      });
  }, []);

  const [cuisineOptions, setCuisineOptions] = useState([]);

  // Responsive cards per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCardsPerView(1);
      } else if (window.innerWidth < 1024) {
        setCardsPerView(2);
      } else {
        setCardsPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter recipes
  useEffect(() => {
    let filtered = [...allRecipes];

    if (filters.portionSize) {
      filtered = filtered.filter(recipe => 
        recipe.portionSize?.toString().includes(filters.portionSize)
      );
    }

    if (filters.cuisine) {
      filtered = filtered.filter(recipe => 
        recipe.cuisine?.toLowerCase().includes(filters.cuisine.toLowerCase())
      );
    }

    if (filters.highProtein === true || filters.highProtein === 'true') {
      filtered = filtered.filter(recipe => recipe.highProtein === true);
    }

    if (filters.budgetFriendly === true || filters.budgetFriendly === 'true') {
      filtered = filtered.filter(recipe => recipe.budgetFriendly === true);
    }

    if (filters.vegan !== 'all') {
      filtered = filtered.filter(recipe => 
        filters.vegan === 'yes' ? recipe.vegan === true : recipe.vegan !== true
      );
    }

    if (filters.maxCalories) {
      filtered = filtered.filter(recipe => {
        const calories = parseInt(recipe.calories);
        return !isNaN(calories) && calories <= parseInt(filters.maxCalories);
      });
    }

    if (filters.difficulty && filters.difficulty.length > 0) {
      filtered = filtered.filter(recipe => {
        const recipeDifficulty = recipe.difficulty?.toLowerCase();
        if (Array.isArray(filters.difficulty)) {
          return filters.difficulty.some(d => d.toLowerCase() === recipeDifficulty);
        }
        return recipeDifficulty === filters.difficulty.toLowerCase();
      });
    }

    if (filters.prepTime) {
      filtered = filtered.filter(recipe => {
        const time = parseInt(recipe.cookingMinutes);
        return !isNaN(time) && time <= parseInt(filters.prepTime);
      });
    }

    setFilteredRecipes(filtered);
    setCurrentIndex(0);
    // reset paging to first page when filters change
    setCurrentPage(1);
  }, [filters, allRecipes]);

  const maxIndex = Math.max(0, filteredRecipes.length - cardsPerView);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || filteredRecipes.length <= cardsPerView) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, currentIndex, filteredRecipes.length, cardsPerView]);

  // Pause auto-play on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  // Measure container width for drag/translate calculations
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.clientWidth);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Touch / drag handlers for carousel
  const handleTouchStart = (e) => {
    if (layout !== 'carousel') return;
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
    setDragDelta(0);
    setIsAutoPlaying(false);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const delta = e.touches[0].clientX - dragStartX;
    setDragDelta(delta);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    setIsAutoPlaying(true);
    const threshold = Math.max(50, containerWidth * 0.15);
    if (Math.abs(dragDelta) > threshold) {
      if (dragDelta > 0) prevSlide(); else nextSlide();
    }
    setDragDelta(0);
  };

  // Mouse drag support (desktop)
  useEffect(() => {
    let moving = false;
    const onMouseMove = (e) => {
      if (!moving) return;
      const delta = e.clientX - dragStartX;
      setDragDelta(delta);
    };

    const onMouseUp = () => {
      if (!moving) return;
      moving = false;
      setIsDragging(false);
      setIsAutoPlaying(true);
      const threshold = Math.max(50, containerWidth * 0.15);
      if (Math.abs(dragDelta) > threshold) {
        if (dragDelta > 0) prevSlide(); else nextSlide();
      }
      setDragDelta(0);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    if (isDragging) {
      moving = true;
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStartX, dragDelta, containerWidth]);

  const handleMouseDown = (e) => {
    if (layout !== 'carousel') return;
    e.preventDefault();
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragDelta(0);
    setIsAutoPlaying(false);
  };

  // Pagination state (used for grid layout)
  const [currentPage, setCurrentPage] = useState(1);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      portionSize: '',
      cuisine: '',
      highProtein: false,
      budgetFriendly: false,
      vegan: 'all',
      maxCalories: '',
      difficulty: '',
      prepTime: ''
    });
  };

  const handleSearchClick = () => {
    setIsFilterExpanded(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.portionSize) count++;
    if (filters.cuisine) count++;
    if (filters.highProtein === true || filters.highProtein === 'true') count++;
    if (filters.budgetFriendly === true || filters.budgetFriendly === 'true') count++;
    if (filters.vegan !== 'all') count++;
    if (filters.maxCalories) count++;
    if (filters.difficulty) count++;
    if (filters.prepTime) count++;
    return count;
  };

  const getActiveFiltersText = () => {
    const active = [];
    if (filters.cuisine) active.push(filters.cuisine);
    if (filters.difficulty && filters.difficulty.length > 0) {
      if (Array.isArray(filters.difficulty)) {
        const formatted = filters.difficulty.map(d => d.charAt(0).toUpperCase() + d.slice(1));
        active.push(formatted.join(', '));
      } else {
        active.push(filters.difficulty.charAt(0).toUpperCase() + filters.difficulty.slice(1));
      }
    }
    if (filters.vegan === 'yes') active.push('Vegan');
    if (filters.highProtein === true || filters.highProtein === 'true') active.push('High Protein');
    if (filters.budgetFriendly === true || filters.budgetFriendly === 'true') active.push('Budget-Friendly');
    if (filters.maxCalories) active.push(`≤${filters.maxCalories} cal`);
    if (filters.prepTime) active.push(`≤${filters.prepTime} min`);
    if (filters.portionSize) active.push(`${filters.portionSize} portions`);
    
    if (active.length === 0) return 'No filters applied';
    if (active.length <= 2) return active.join(', ');
    return `${active.slice(0, 2).join(', ')} +${active.length - 2} more`;
  };

  if (allRecipes.length === 0) {
    return (
      <>
      <div className="w-full py-12 text-center text-gray-500">
        <p>Loading recipes...</p>
          </div>
        </>
      )}

        {/* Dot Indicators */}
  const showNavigation = filteredRecipes.length > cardsPerView;

  return (
    <div className="w-full py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col gap-6">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">All Recipes</h2>
            <div className="h-1 w-20 bg-gradient-to-r from-pink-600 to-purple-600 mt-3 rounded-full" />
          </div>

          {/* Filter Panel (extracted) */}
          {showFilterPanel && (
            <>
              {/* Compact Filter Summary */}
              {!isFilterExpanded && (
                <div 
                  className="bg-white rounded-xl shadow-md p-4 border border-gray-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setIsFilterExpanded(true)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg">
                        <FontAwesomeIcon icon={faFilter} className="text-white text-sm" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Filters</div>
                        <div className="text-sm text-gray-600">{getActiveFiltersText()}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getActiveFiltersCount() > 0 && (
                        <div className="flex items-center justify-center w-8 h-8 bg-pink-600 text-white rounded-full text-sm font-bold">
                          {getActiveFiltersCount()}
                        </div>
                      )}
                      <FontAwesomeIcon icon={faChevronDown} className="text-gray-400" />
                    </div>
                  </div>
                </div>
              )}

              {/* Expanded Filter Panel */}
              {isFilterExpanded && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faFilter} className="text-pink-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Adjust Filters</h3>
                    </div>
                    <button
                      onClick={() => setIsFilterExpanded(false)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                    >
                      <FontAwesomeIcon icon={faChevronUp} />
                      Collapse
                    </button>
                  </div>
                  <FilterPanel
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    showSearchButton={true}
                    onSearch={handleSearchClick}
                    cuisineOptions={cuisineOptions}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Results Container: carousel or grid */}
      <div 
        className="relative max-w-7xl mx-auto"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >

        {layout === 'grid' ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredRecipes.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((recipe, index) => (
                <div key={recipe.id || index} className="flex justify-center">
                  <RecipeCard recipe={recipe} />
                </div>
              ))}
            </div>

            {/* Pagination controls */}
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">Showing {Math.min((currentPage - 1) * pageSize + 1, filteredRecipes.length)}-{Math.min(currentPage * pageSize, filteredRecipes.length)} of {filteredRecipes.length} recipes</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-white border rounded disabled:opacity-50"
                >
                  Prev
                </button>

                {[...Array(Math.max(1, Math.ceil(filteredRecipes.length / pageSize)))].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1 rounded ${currentPage === i + 1 ? 'bg-pink-600 text-white' : 'bg-white border'}`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(Math.ceil(filteredRecipes.length / pageSize), p + 1))}
                  disabled={currentPage >= Math.ceil(filteredRecipes.length / pageSize)}
                  className="px-3 py-1 bg-white border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
        <>
        {/* Navigation Buttons */}
        {showNavigation && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-40 w-12 h-12 rounded-full bg-white shadow-xl hover:bg-gray-50 transition-all hover:scale-110 flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentIndex === 0}
              aria-label="Previous slide"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-gray-700 group-hover:text-pink-600 transition-colors" />
            </button>

            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-40 w-12 h-12 rounded-full bg-white shadow-xl hover:bg-gray-50 transition-all hover:scale-110 flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentIndex >= maxIndex}
              aria-label="Next slide"
            >
              <FontAwesomeIcon icon={faChevronRight} className="text-gray-700 group-hover:text-pink-600 transition-colors" />
            </button>
          </>
        )}

          {/* Cards Container */}
          <div
            ref={containerRef}
            className={`overflow-hidden ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
          >
            <div
              className="flex transition-transform duration-500 ease-out gap-6"
              style={{
                transform: (() => {
                  const basePercent = currentIndex * (100 / cardsPerView);
                  const offsetPercent = containerWidth ? (dragDelta * 100) / containerWidth : 0;
                  return `translateX(calc(-${basePercent}% + ${offsetPercent}%))`;
                })()
              }}
            >
              {filteredRecipes.map((recipe, index) => (
                <div
                  key={recipe.id || index}
                  className="flex-shrink-0"
                  style={{
                    width: cardsPerView === 1 ? '100%' : cardsPerView === 2 ? 'calc(50% - 12px)' : 'calc(33.333% - 16px)'
                  }}
                >
                  <div className="flex justify-center">
                    <RecipeCard recipe={recipe} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
        )}
        
        {/* Dot Indicators */}
        {showNavigation && (
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(maxIndex + 1)].map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all rounded-full ${
                  currentIndex === index
                    ? 'w-8 h-2 bg-pink-600'
                    : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

export default AllRecipes;